import { axiosInstance, ENDPOINTS, API_BASE_URL } from './api.js'; // Adjust path
import { joinStreamingResponse } from './models/joinStreamingResponse';
import { LoginResponse } from './models/LoginResponse.ts';
import { RegisterResponse } from './models/RegisterResponse.ts';
import { TermsResponse } from './models/TermsResponse.ts';
import { PrivacyResponse } from './models/PrivacyResponse.ts';
import { WelcomeResponse } from './models/WelcomeResponse.ts';
import { OnlineUserResponse } from './models/OnlineUserResponse';
import { coinHistoryResponse } from './models/coinHistoryResponse.ts';
import { otpVerificationResponse } from './models/otpVerificationResponse.ts'
import { resendOtpResponse } from './models/resendOtpResponse.ts'
import { setupProfileResponse } from './models/setupProfileResponse.ts';
import { forgotPasswordResponse } from './models/forgotPasswordResponse.ts';
import { otpForgotPasswordResponse } from './models/otpForgotPasswordResponse.ts';
import { resetPasswordResponse } from './models/resetPasswordResponse.ts';
import { changePasswordResponse } from './models/changePasswordResponse.ts';
import { ProfileResponse } from './models/ProfileResponse.ts';
import { updateProfileResponse } from './models/updateProfileResponse.ts';
import { giftSendResponse } from './models/giftSendResponse.ts';
import { getUserInfoResponse } from './models/getUserInfoResponse.ts';
import { followResponse } from './models/followResponse.ts';
import { followerListResponse } from './models/followerListResponse.ts';
import { liveStreamingListResponse } from './models/liveStreamingListResponse.ts';
import { streamingStartResponse } from './models/streamingStartResponse.ts';
import { getChatResponse } from './models/getChatResponse.ts';
import { sendMessagesResponse } from './models/sendMessagesResponse.ts';
import { markAsReadMsgResponse } from './models/markAsReadMsgResponse.ts';
import { CardsResponse, SaveCardResponse, DeleteCardResponse, Card } from './models/cardsResponse.ts';
import { PurchaseHistoryResponse, CoinPurchaseResponse, Purchase } from './models/purchaseHistoryResponse.ts';
import { CryptoPurchaseResponse, PurchaseStatusResponse, CryptoPaymentStatus } from './models/cryptoPurchaseResponse.ts';
import { SocialLoginResponse } from './models/socialLoginResponse.ts';
import { SlidesResponse } from './models/slidesResponse.ts';
// import { streamingStartResponse } from './models/streamingStartResponse.ts';
// import AsyncStorage from '@react-native-async-storage/async-storage';

class AuthService {

  //LOGIN
  static async login(email: string, password: string, fcm_device_token : string, recaptcha?: string): Promise<LoginResponse> {
    if (!email || email.trim() === '') {
      throw new Error('email is required');
    }

    try {
      const payload: Record<string, unknown> = { email, password, fcm_device_token };
      if (recaptcha) payload.recaptcha = recaptcha;
      const response = await axiosInstance.post<LoginResponse>(ENDPOINTS.AUTH.LOGIN, payload);
      return response.data;
    } catch (error) {
      console.error('AuthService - Login failed:', error);
      throw error;
    }
  }

  // 2FA — exchange challenge_token + TOTP code for a real token
  static async verifyLogin2fa(challenge_token: string, code: string, fcm_device_token?: string) {
    const response = await axiosInstance.post(ENDPOINTS.AUTH.TWO_FA_VERIFY_LOGIN, {
      challenge_token,
      code,
      fcm_device_token,
    });
    return response.data;
  }

  // 2FA — begin enrollment (returns { qr, secret })
  static async enable2fa() {
    const response = await axiosInstance.post(ENDPOINTS.AUTH.TWO_FA_ENABLE, {});
    return response.data;
  }

  // 2FA — confirm enrollment after scanning QR
  static async confirm2fa(secret: string, code: string) {
    const response = await axiosInstance.post(ENDPOINTS.AUTH.TWO_FA_CONFIRM, { secret, one_time_password: code });
    return response.data;
  }

  // 2FA — turn off
  static async disable2fa(password: string) {
    const response = await axiosInstance.post(ENDPOINTS.AUTH.TWO_FA_DISABLE, { password });
    return response.data;
  }

  // Session — revoke every Passport token for the user (PDF "logout from all devices")
  static async logoutAllDevices() {
    const response = await axiosInstance.post(ENDPOINTS.AUTH.LOGOUT_ALL, {});
    return response.data;
  }

  // Session — standard logout of current device
  static async logout() {
    const response = await axiosInstance.post(ENDPOINTS.AUTH.LOGOUT, {});
    return response.data;
  }

  //REGISTRATION
  static async register(
    full_name: string,
    phone_number: string,
    email: string,
    birthdate: string,
    password: string,
    password_confirmation: string,
    fcm_device_token: string,
    language_preference: string = 'en',
    accepted_terms: boolean = false,
    recaptcha?: string,
  ): Promise<RegisterResponse> {
    try {
      const payload: Record<string, unknown> = {
        full_name,
        phone_number,
        email,
        birthdate,
        password,
        password_confirmation,
        fcm_device_token,
        language_preference,
        accepted_terms: !!accepted_terms,
      };
      if (recaptcha) payload.recaptcha = recaptcha;

      const response = await axiosInstance.post<RegisterResponse>(ENDPOINTS.REGISTER, payload);
      console.log('AuthService - Register response:', response);
      return response.data;
    } catch (error) {
      console.log('AuthService - Register failed:', error);
      throw error;
    }
  }


    //GOOGLE SIGN IN
    static async googleSocialLogin(
      google_token: string,
      full_name: string,
      email: string,
      google_id: string,
      fcm_device_token: string,
    ): Promise<SocialLoginResponse> {
      try {
        const formData = new FormData();
        formData.append("email", email);
        formData.append("google_token", google_token);
        formData.append("full_name", full_name);
        formData.append("google_id", google_id);
        formData.append("fcm_device_token", fcm_device_token);

        const response = await axiosInstance.post<SocialLoginResponse>(
          ENDPOINTS.GOOGLE_SOCIAL_LOGIN,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        return response.data;
      } catch (error) {
        throw error;
      }
    }

    //APPLE SIGN IN
    static async appleSocialLogin(
      apple_token: string,
      full_name: string,
      email: string,
      apple_id: string,
      fcm_device_token: string,
    ): Promise<SocialLoginResponse> {
      try {
        const formData = new FormData();
        formData.append("apple_token", apple_token);
        formData.append("email", email);
        formData.append("apple_id", apple_id);
        formData.append("fcm_device_token", fcm_device_token);

        const response = await axiosInstance.post<SocialLoginResponse>(
          ENDPOINTS.APPLE_SOCIAL_LOGIN,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        return response.data;
      } catch (error) {
        throw error;
      }
    }

  static async terms(): Promise<TermsResponse> {
    try {
      const response = await axiosInstance.get<TermsResponse>(ENDPOINTS.TERMS);
      return response.data;
    } catch (error) {
      console.error('AuthService - Terms failed:', error);
      throw error;
    }
  }

  static async privacy(): Promise<PrivacyResponse> {
    try {
      const response = await axiosInstance.get<PrivacyResponse>(ENDPOINTS.PRIVACY);
      return response.data;
    } catch (error) {
      console.error('AuthService - Privacy failed:', error);
      throw error;
    }
  }

  static async welcome(): Promise<WelcomeResponse> {
    try {
      const response = await axiosInstance.get<WelcomeResponse>(ENDPOINTS.WELCOME_SCREEN);
      return response.data;
    } catch (error) {
      console.error('AuthService - Terms failed:', error);
      throw error;
    }
  }

  static async onlineUsers(): Promise<OnlineUserResponse> {
    try {
      const response = await axiosInstance.get<OnlineUserResponse>(ENDPOINTS.ONLINE_USERS);
      console.log('online users response:', response.data)
      return response.data;
    } catch (error) {
      //console.error('AuthService - onlineUsers failed:', error);
      throw error;
    }
  }

  // Home banner carousel slides (public endpoint, no auth required).
  static async getSlides(): Promise<SlidesResponse> {
    const response = await axiosInstance.get<SlidesResponse>(ENDPOINTS.SLIDES);
    return response.data;
  }

  static async coinHistory(): Promise<coinHistoryResponse> {
    try {
      const response = await axiosInstance.get<coinHistoryResponse>(ENDPOINTS.COIN_HISTORY);
      console.log('coinHistory response:', response.data)
      return response.data;
    } catch (error) {
      //console.error('AuthService - onlineUsers failed:', error);
      throw error;
    }
  }

  //wallet new api
  static async coinWallet(): Promise<walletResponse> {
      try {
        const response = await axiosInstance.get<walletResponse>(ENDPOINTS.WALLET);
        console.log('walletResponse:', response.data)
        return response.data;
      } catch (error) {
        //console.error('AuthService - onlineUsers failed:', error);
        throw error;
      }
  }

  //USER PROFILE DATA
  static async userProfile(): Promise<ProfileResponse> {
    try {
      const response = await axiosInstance.get<ProfileResponse>(ENDPOINTS.PROFILE);
      console.log('profile response:', response.data)
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Another user's profile via the PROFILE endpoint with their id passed as a
  // query param (the /mobile/profile/{id} path form doesn't exist on the backend).
  static async getProfileById(userId: number | string): Promise<ProfileResponse> {
    const response = await axiosInstance.get<ProfileResponse>(ENDPOINTS.PROFILE, {
      params: { user_id: userId },
    });
    console.log('profile-by-id response:', response.data);
    return response.data;
  }

  static async getNotifications(): Promise<notificationListResponse> {
    try {
      const response = await axiosInstance.get<notificationListResponse>(ENDPOINTS.GET_NOTIFICATION_LIST);
      console.log('getNotifications response:', response.data)
      return response.data;
    } catch (error) {
      //console.error('AuthService - onlineUsers failed:', error);
      throw error;
    }
  }

static async cancelSubscription(reason?: string): Promise<void> {
  try {
    interface CancelSubscriptionRequest {
      reason: string;
    }

    const requestBody: CancelSubscriptionRequest = {
      reason: reason && reason.trim().length > 0 ? reason.trim() : "No longer needed",
    };

    const response = await axiosInstance.post(
      ENDPOINTS.SUBSCRIPTION_CANCEL,
      requestBody
    );

    console.log('cancelSubscription response:', response.data);
    return;
  } catch (error) {
    console.error('Error canceling subscription:', error);
    throw error;
  }
}

//subscription plan
static async getSubscriptionPlan(): Promise<subscriptionPlanResponse> {
    try {
      const response = await axiosInstance.get<subscriptionPlanResponse>(ENDPOINTS.SUBSCRIPTION_PLANS);
      console.log('getSubscriptionPlan response:', response.data)
      return response.data;
    } catch (error) {
      //console.error('AuthService - onlineUsers failed:', error);
      throw error;
    }
  }

  //subscription status
static async getSubscriptionStatus(): Promise<subscriptionStatusResponse> {
    try {
      const response = await axiosInstance.get<subscriptionStatusResponse>(ENDPOINTS.SUBSCRIPTION_STATUS);
      console.log('getSubscriptionStatus response:', response.data)
      return response.data;
    } catch (error) {
      //console.error('AuthService - onlineUsers failed:', error);
      throw error;
    }
  }

  static async purchaseSubscription(
    plan_id: string | number,
    platform: string,
    platform_subscription_id: string,
    platform_transaction_id: string,
    platform_receipt: string,
    amount_paid: string | number
  ): Promise<purchaseSubResponse> {
    try {
      const payload = {
        plan_id,
        platform,
        platform_subscription_id,
        platform_transaction_id,
        platform_receipt,
        amount_paid,
      };
      const response = await axiosInstance.post<purchaseSubResponse>(ENDPOINTS.PURCHASE_SUBSCRIPTION, payload);
      console.log('purchaseSubscription response:', response.data)
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Purchase subscription using Stripe (for web)
  static async purchaseSubscriptionStripe(
    plan_id: string | number,
    amount_paid: string | number,
    payment_method_id: string,
    card_id?: number,
    google_plan_id?: string
  ): Promise<purchaseSubResponse> {
    try {
      // For Stripe web payments
      // Backend requires all platform fields but should check payment_method='stripe'
      // and skip IAP verification
      // Generate unique IDs to prevent duplicate entry errors
      const uniqueTimestamp = Date.now();
      const uniqueId = `${uniqueTimestamp}_${Math.random().toString(36).substring(2, 9)}`;

      const payload: any = {
        plan_id,
        amount_paid,
        payment_method: 'stripe',
        stripe_payment_method_id: payment_method_id,
        platform: 'stripe',
        platform_transaction_id: `stripe_web_${uniqueId}`,
        platform_subscription_id: `stripe_sub_${plan_id}_${uniqueId}`,
        platform_receipt: `STRIPE_WEB_${uniqueId}`, // Unique receipt to prevent duplicate entries
      };

      // Only add card_id if provided
      if (card_id) {
        payload.card_id = card_id;
      }

      console.log('purchaseSubscriptionStripe payload:', payload);
      const response = await axiosInstance.post<purchaseSubResponse>(ENDPOINTS.PURCHASE_SUBSCRIPTION, payload);
      console.log('purchaseSubscriptionStripe response:', response.data);
      return response.data;
    } catch (error) {
      console.error('purchaseSubscriptionStripe error:', error);
      throw error;
    }
  }

  static async generateToken(
    user_id: string ,
    token: string,
    expires_in: string,
    redirect_path: string,
  ): Promise<purchaseSubResponse> {
    try {
      const payload = {
        user_id,
        token,
        expires_in,
        redirect_path,
      };
      const response = await axiosInstance.post<purchaseSubResponse>(ENDPOINTS.TOKEN_GENERATE, payload);
      console.log('purchaseSubscgenerateTokenription response:', response.data)
      return response.data;
    } catch (error) {
      throw error;
    }
  }



  static async markAsReadNotification(): Promise<readNotificationResponse> {
    try {
      const response = await axiosInstance.post<readNotificationResponse>(ENDPOINTS.NOTIFICATION_READ, {  });
      console.log('markAsReadNotification response:', response.data)
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // KYC Initiate -> returns URL (iframe)
  static async kycInitiate(): Promise<any> {
    try {
      const response = await axiosInstance.get(ENDPOINTS.KYC_INITIATE);
      return response.data;
    } catch (error) {
      throw error;
    }
  }


    static async deleteNotification(notificationId: string): Promise<any> {
      try {
        const url = ENDPOINTS.DELETE_SPECIFIC_NOTIFICATION(String(notificationId));
        const response = await axiosInstance.delete<any>(url);
        console.log(`Notification ID ${notificationId} deleted successfully:`, response.data);
        return response.data;
      } catch (error) {
        console.error(`Error deleting notification ID ${notificationId}:`, error);
        throw error;
      }
    }


    static async clearAllNotification(): Promise<any> {
      try {
        const url = ENDPOINTS.CLEAR_ALL_NOTIFICAITON;
        const response = await axiosInstance.delete<any>(url);
        console.log(`clearAll deleted successfully:`, response.data);
        return response.data;
      } catch (error) {
        console.error(`Error deleting notification ID :`, error);
        throw error;
      }
    }

  //OTP VERIFICATION
  static async otpVerify(email: string, otp: string): Promise<otpVerificationResponse> {
    try {
      const response = await axiosInstance.post<otpVerificationResponse>(ENDPOINTS.OTP_VERIFICATION, { email, otp });
      console.log('otpVerify response:', response.data)
      return response.data;
    } catch (error) {
      throw error;
    }
  }


  //RESEND OTP
  static async resendOtp(email: string): Promise<resendOtpResponse> {
    try {
      console.log('resendOtp called with email:', email);
      console.log('Using endpoint:', ENDPOINTS.RESEND_OTP);

      const response = await axiosInstance.post<resendOtpResponse>(ENDPOINTS.RESEND_OTP, { email });
      console.log('resendOtp response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('resendOtp error:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      console.error('Error message:', error.message);
      throw error;
    }
  }

  //SETUP PROFILE WITHOUT IMAGE 
  static async setupProfile(
    user_id: string,
    display_name: string,
    language_preference: string
  ): Promise<setupProfileResponse> {
    try {
      console.log('setupProfile called with:', { user_id, display_name, language_preference });
      console.log('Endpoint:', ENDPOINTS.SETUP_PROFILE_IMAGE);

      const formData = new FormData();
      formData.append('user_id', user_id);
      formData.append('display_name', display_name);
      formData.append('language_preference', language_preference);

      console.log('FormData prepared for setupProfile');

      const response = await axiosInstance.post<setupProfileResponse>(
        ENDPOINTS.SETUP_PROFILE_IMAGE,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      console.log('setupProfile response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('setupProfile error:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      console.error('Error message:', error.message);
      throw error;
    }
  }



  //SETUP PROFILE WITH IMAGE 
  static async setupProfileImg(
    user_id: string,
    display_name: string,
    language_preference: string,
    profile_picture: { uri: string; name: string; type: string }
  ): Promise<setupProfileResponse> {
    try {
      console.log('setupProfileImg called with:', { user_id, display_name, language_preference, profile_picture });
      console.log('Endpoint:', ENDPOINTS.SETUP_PROFILE_IMAGE);

      const formData = new FormData();
      formData.append('user_id', user_id);
      formData.append('display_name', display_name);
      formData.append('language_preference', language_preference);
      formData.append('profile_picture', {
        uri: profile_picture.uri,
        name: profile_picture.name,
        type: profile_picture.type,
      } as any);

      console.log('FormData prepared for setupProfileImg');

      const response = await axiosInstance.post<setupProfileResponse>(
        ENDPOINTS.SETUP_PROFILE_IMAGE,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      console.log('setupProfileImg response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('setupProfileImg error:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      console.error('Error message:', error.message);
      throw error;
    }
  }


  //FORGOT PASSWORD
  static async forgotPassword(email: string): Promise<forgotPasswordResponse> {
    try {
      const response = await axiosInstance.post<forgotPasswordResponse>(ENDPOINTS.FORGOT_PASSWORD, { email });
      console.log('forgotPassword response:', response.data)
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // OTP FORGOT PASSWORD
  static async otpForgotPassword(email: string, otp: string): Promise<otpForgotPasswordResponse> {
    try {
      const response = await axiosInstance.post<otpForgotPasswordResponse>(ENDPOINTS.OTP_FORGOT_PASSWORD, { email, otp });
      console.log('otpForgotPassword response:', response.data)
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  //RESET PASSWORD
  static async resetPassword(email: string, password: string, password_confirmation: string): Promise<resetPasswordResponse> {
    try {
      const response = await axiosInstance.post<resetPasswordResponse>(ENDPOINTS.RESET_PASSWORD, { email, password, password_confirmation });
      console.log('resetPassword response:', response.data)
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  //CHANGE PASSWORD
  static async changePassword(old_password: string, new_password: string, new_password_confirmation: string): Promise<changePasswordResponse> {
    try {
      const response = await axiosInstance.post<changePasswordResponse>(ENDPOINTS.CHANGE_PASSWORD, { old_password, new_password, new_password_confirmation });
      console.log('changePassword response:', response.data)
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  //UPDATE PROFILE DATA WITHOUT IMAGE
  static async updateProfile(phone: string, display_name: string, birthdate: string, language_preference: string): Promise<updateProfileResponse> {
    try {
      const response = await axiosInstance.post<updateProfileResponse>(ENDPOINTS.UPDATE_PROFILE, { phone, display_name, birthdate, language_preference });
      console.log('updateProfile response:', response.data)
      return response.data;
    } catch (error) {
      throw error;
    }
  }


  //UPDATE PROFILE DATA WITH IMAGE
  static async updateProfileImg(
    phone: string,
    display_name: string,
    birthdate: string,
    language_preference: string,
    profile_picture: { uri: string; name?: string; fileName?: string; type: string }
  ): Promise<updateProfileResponse> {
    try {
      const formData = new FormData();

      formData.append('phone', String(phone));
      formData.append('display_name', display_name);
      formData.append('birthdate', birthdate);
      formData.append('language_preference', language_preference);

        formData.append('profile_picture', {
          uri: Platform.OS === 'ios' && !profile_picture.uri.startsWith('file://')
            ? `file://${profile_picture.uri}`
            : profile_picture.uri,
          name: profile_picture.fileName || profile_picture.name || 'profile.jpg',
          type: profile_picture.type || 'image/jpeg',
        } as any);


      console.log('❌ updateProfileImg response auth:', profile_picture);

      // ✅ Debug: log FormData parts
      (formData as any)._parts?.forEach((p: any) => console.log('📦', p));

      const response = await axiosInstance.post<updateProfileResponse>(
        ENDPOINTS.UPDATE_PROFILE,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          transformRequest: (data) => data, // 👈 IMPORTANT for RN
        }
      );

      return response.data;
    } catch (error: any) {
      if (error.response) {
        console.error('❌ API Error:', error.response.data);
      } else {
        console.error('❌ updateProfileImg error:', error.message);
      }
      throw error;
    }
  }




  //LIVE STREAMING LIST
  static async liveStreamingList(): Promise<liveStreamingListResponse> {
    try {
      const response = await axiosInstance.get<liveStreamingListResponse>(ENDPOINTS.LIVE_STREAMING_LIST);
      console.log('liveStreamingLsit response:', response.data)
      return response.data;
    } catch (error) {
      //console.error('AuthService - liveStreamingLsit failed:', error);
      throw error;
    }
  }

  //GIFT SEND
  static async giftSend(live_id: string, gift_id: string, quantity: string): Promise<giftSendResponse> {
    try {
      const response = await axiosInstance.post<giftSendResponse>(ENDPOINTS.GIFT_SEND, { live_id, gift_id, quantity });
      console.log('giftSend response:', response.data)
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Send a gift to a specific user (chat). Uses receiver_id instead of live_id.
  static async sendGiftToUser(receiver_id: number | string, gift_id: number | string, quantity: number = 1): Promise<giftSendResponse> {
    try {
      const payload = {
        receiver_id: Number(receiver_id),
        gift_id: Number(gift_id),
        quantity: Number(quantity) || 1,
      };
      console.log('sendGiftToUser payload:', payload);
      const response = await axiosInstance.post<giftSendResponse>(ENDPOINTS.GIFT_SEND, payload);
      console.log('sendGiftToUser response:', response.data);
      return response.data;
    } catch (error) {
      console.error('sendGiftToUser error:', error);
      throw error;
    }
  }


  //GIFT LIST
  static async giftList(): Promise<giftListResponse> {
    try {
      const response = await axiosInstance.get<giftListResponse>(ENDPOINTS.GIFT_LIST);
      console.log('giftList response:', response.data)
      return response.data;
    } catch (error) {
      throw error;
    }
  }


  //FOLLOWER LIST
  static async followersList(): Promise<followerListResponse> {
    try {
      const response = await axiosInstance.get<followerListResponse>(ENDPOINTS.FOLLOWERS_LIST);
      console.log('followersList response:', response.data)
      return response.data;
    } catch (error) {
      //console.error('AuthService - followersList failed:', error);
      throw error;
    }
  }


  //FOLLOWING LIST
  static async followingList(): Promise<followerListResponse> {
    try {
      const response = await axiosInstance.get<followerListResponse>(ENDPOINTS.FOLLOWING_LIST);
      console.log('followingList response:', response.data)
      return response.data;
    } catch (error) {
      //console.error('AuthService - followersList failed:', error);
      throw error;
    }
  }

    //COIN LIST
    static async coinList(): Promise<coinBuyResponse> {
      try {
        const response = await axiosInstance.get<coinBuyResponse>(ENDPOINTS.COINS_LIST);
        console.log('coinList response:', response.data)
        return response.data;
      } catch (error) {
        console.error('AuthService - coinList failed:', error);
        throw error;
      }
    }


  //GET USER BY ID
  static async getUserInfo(userId: number): Promise<getUserInfoResponse> {
    try {
      const response = await axiosInstance.get<getUserInfoResponse>(
        `${ENDPOINTS.GET_USER_INFO}/${userId}`
      );
      console.log('getUserInfo response:', response.data);
      return response.data;
    } catch (error) {
      throw error;
    }
  }


  //CREDIT COIN
  static async creditCoin(amount: string): Promise<giftSendResponse> {
    try {
      const response = await axiosInstance.post<giftSendResponse>(ENDPOINTS.CREDIT_COIN, { amount });
      console.log('creditCoin response:', response.data)
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // VERIFY IAP RECEIPT AND CREDIT COINS
  static async verifyAndCreditCoin(payload: any): Promise<any> {
    try {
      const response = await axiosInstance.post<any>(ENDPOINTS.VERIFY_IAP, payload);
      console.log('verifyAndCreditCoin response:', response.data);
      return response.data;
    } catch (error) {
      throw error;
    }
  }


  //FOLLOW
  static async followAccount(followingID: string): Promise<followResponse> {
    console.log('followAccount function called with ID:', followingID);
    try {
      const formData = new FormData();
      formData.append('following_id', followingID);

      const response = await axiosInstance.post<followResponse>(
        ENDPOINTS.FOLLOW_ACCOUNT,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      return response.data;
    } catch (error) {
      throw error;
    }
  }

  //UNFOLLOW
  static async unfollowAccount(followingID: string): Promise<followResponse> {
    console.log('unfollowAccount function called with ID:', followingID);
    try {
      const formData = new FormData();
      formData.append('following_id', followingID);

      const response = await axiosInstance.post<followResponse>(
        ENDPOINTS.UNFOLLOW_ACCOUNT,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // START LIVE STREAMING
  // static async streamingStart(channel_id: string, status: string, category: string, duration_limit: string) {
  //   try {
  //     const response = await axiosInstance.post(ENDPOINTS.LIVE_STREAMING_START, {
  //       channel_id,
  //       status,
  //       category,
  //       duration_limit,
  //     });

  //     return response.data; // return parsed JSON
  //   } catch (err: any) {
  //     console.error("❌ streamingStart error:", err?.response?.data || err.message);
  //     throw err;
  //   }
  // }

  static async joinStreaming(streamingID: string): Promise<joinStreamingResponse> {
    try {
      console.log('🔗 Calling joinStreaming API for streamID:', streamingID);
      // Correct path: /api/mobile/livestreams/{id}/join (baseURL already set)
      const endpoint = `/mobile/livestreams/${streamingID}/join`;
      const response = await axiosInstance.post<joinStreamingResponse>(endpoint);

      const data = response.data;
      console.log('✅ joinStreaming response:', JSON.stringify(data, null, 2));

      // Validate response structure
      if (!data) {
        throw new Error('Empty response from server');
      }

      if (data.status === 'error') {
        throw new Error(data.message || 'Stream join failed');
      }

      if (!data.agoratoken) {
        console.error('⚠️ Response missing agoratoken:', data);
        throw new Error('Token not provided by server');
      }

      return data;
    } catch (error: any) {
      console.error('❌ joinStreaming API Error:', {
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status
      });

      // Extract meaningful error message
      const apiError = error?.response?.data?.message || error?.message || 'Network error';
      throw new Error(apiError);
    }
  }

  // Get viewer count for a stream
  static async getViewCount(streamingID: string): Promise<{ viewer_count: number }> {
    try {
      console.log('📊 Fetching viewer count for stream:', streamingID);
      const response = await axiosInstance.get(`${ENDPOINTS.VIEW_COUNT}?stream_id=${streamingID}`);
      console.log('✅ View count response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ getViewCount API Error:', error?.message);
      throw error;
    }
  }

  // HOST-ONLY keepalive. Tells the backend the broadcast is still alive so it
  // isn't reaped as a zombie stream. `streamingID` is optional — when omitted
  // the backend resolves the user's currently active stream. Viewers must NOT
  // call this. The Bearer token is attached automatically by the axios
  // request interceptor.
  static async sendHeartbeat(streamingID?: string | number): Promise<any> {
    const body =
      streamingID !== undefined && streamingID !== null && String(streamingID) !== ''
        ? { id: String(streamingID) }
        : {};
    const response = await axiosInstance.post(ENDPOINTS.LIVE_STREAMING_HEARTBEAT, body);
    return response.data;
  }

  static async streamingStart(channel_id: string, status: string, category: string, title : string ,duration_limit: string): Promise<streamingStartResponse> {
    console.log('LIVE_STREAMING_START channel_id:', channel_id, status, category, duration_limit);
    try {
      const formData = new FormData();
      formData.append('channel_id', channel_id);
      formData.append('status', status);
      formData.append('category', category);
      formData.append('duration_limit', duration_limit);

      const response = await axiosInstance.post<streamingStartResponse>(
        ENDPOINTS.LIVE_STREAMING_START,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // STOP LIVE STREAMING
  static async streamingStop(id: string, status: string) {
    try {
      const response = await axiosInstance.post(ENDPOINTS.LIVE_STREAMING_STOP, {
        id,
        status,
      });

      return response.data; // return parsed JSON
    } catch (err: any) {
      console.error("❌ streamingStart error:", err?.response?.data || err.message);
      throw err;
    }
  }


  //SEND MESSAGE
  static async sendMessage(receiver_id: string, message: string): Promise<sendMessagesResponse> {
    try {
      const formData = new FormData();
      formData.append('receiver_id', receiver_id);
      formData.append('message', message);

      const response = await axiosInstance.post<sendMessagesResponse>(
        ENDPOINTS.SEND_MESSAGE,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  //SEND PRIVATE MESSAGE (Unified Chat API)
  static async sendPrivateMessage(receiver_id: string | number, message: string): Promise<any> {
    try {
      console.log('📤 [SEND MESSAGE] Payload:', { receiver_id, message, type: typeof receiver_id });
      const response = await axiosInstance.post(
        ENDPOINTS.SEND_PRIVATE_MESSAGE,
        { receiver_id, message }
      );
      console.log('✅ [SEND MESSAGE] Success:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ [SEND MESSAGE] Failed:', error);
      console.error('📥 [ERROR RESPONSE]:', error.response?.data);
      throw error;
    }
  }


  //MARK MESSAGES AS READ
  static async markMessagesAsRead(roomId: string): Promise<markAsReadMsgResponse> {
    try {
      const endpoint = typeof ENDPOINTS.MARK_READ_MESSAGE === 'function'
        ? ENDPOINTS.MARK_READ_MESSAGE(roomId)
        : ENDPOINTS.MARK_READ_MESSAGE;

      const response = await axiosInstance.post(endpoint);

      if (response.data.status) {
        console.log("✅", response.data.message); // "Messages marked as read"
      } else {
        console.warn("⚠️ Failed:", response.data.message);
      }
      return response.data as markAsReadMsgResponse;
    } catch (error) {
      if (error instanceof Error) {
        console.error("❌ API Error:", error.message);
      } else {
        console.error("❌ API Error:", error);
      }
      throw error;
    }
  }


  //GET CHAT (Unified Rooms)
  static async getChat(): Promise<getChatResponse> {
    try {
      const response = await axiosInstance.get<getChatResponse>(ENDPOINTS.GET_CHAT1);
      console.log('🔍 [DEBUG] Unified chat rooms response:', JSON.stringify(response.data, null, 2));
      return response.data;
    } catch (error) {
      console.error('AuthService - getChat failed:', error);
      throw error;
    }
  }

  //GET MESSAGES (Unified Chat API)
  static async getMessages(roomIdentifier: string): Promise<any> {
    try {
      const endpoint = typeof ENDPOINTS.GET_MESSAGES === 'function'
        ? ENDPOINTS.GET_MESSAGES(roomIdentifier)
        : ENDPOINTS.GET_MESSAGES;

      console.log('[getMessages] Fetching messages for room:', roomIdentifier);
      const response = await axiosInstance.get(endpoint);
      console.log('[getMessages] Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('[getMessages] Error:', error);
      console.error('[getMessages] Error details:', error.response?.data);
      throw error;
    }
  }

  // CATEGORIES
  static async categories(): Promise<any> {
    try {
      const response = await axiosInstance.get<any>(ENDPOINTS.CATEGORIES as any);
      return response.data;
    } catch (error) {
      throw error;
    }
  }


    //GIFT SEND
      static async giftSend(live_id: string, gift_id: string, quantity: string): Promise<sendGiftResponse> {
        try {
          const formData = new FormData();
          formData.append('live_id', live_id);
          formData.append('gift_id', gift_id);
          formData.append('quantity', quantity);

          const response = await axiosInstance.post<sendGiftResponse>(
            ENDPOINTS.GIFT_SEND,
            formData,
            {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            }
          );
          return response.data;
        } catch (error) {
          throw error;
        }
      }



  // PLATFORM ACCOUNTS APIs

  // Get all available platforms
  static async getPlatforms(): Promise<any> {
    try {
      const response = await axiosInstance.get(ENDPOINTS.PLATFORMS);
      console.log('getPlatforms response:', response.data);
      return response.data;
    } catch (error) {
      console.error('getPlatforms error:', error);
      throw error;
    }
  }

  // Get user's saved platform accounts
  static async getPlatformAccounts(): Promise<any> {
    try {
      const response = await axiosInstance.get(ENDPOINTS.PLATFORM_ACCOUNTS);
      console.log('getPlatformAccounts response:', response.data);
      return response.data;
    } catch (error) {
      console.error('getPlatformAccounts error:', error);
      throw error;
    }
  }

  // Add a new platform account.
  // account_number and casino_user_id are sent with the same clean numeric
  // value (both kept for backend compatibility).
  static async addPlatformAccount(platform_key: string, casino_user_id: string): Promise<any> {
    try {
      const clean = String(casino_user_id || '').replace(/\D/g, '');
      const response = await axiosInstance.post(ENDPOINTS.PLATFORM_ACCOUNT, {
        platform_key,
        account_number: clean,
        casino_user_id: clean,
      });
      console.log('addPlatformAccount response:', response.data);
      return response.data;
    } catch (error) {
      console.error('addPlatformAccount error:', error);
      throw error;
    }
  }

  // Delete a platform account
  static async deletePlatformAccount(accountId: number | string): Promise<any> {
    try {
      const url = ENDPOINTS.DELETE_PLATFORM_ACCOUNT(String(accountId));
      const response = await axiosInstance.delete(url);
      console.log('deletePlatformAccount response:', response.data);
      return response.data;
    } catch (error) {
      console.error('deletePlatformAccount error:', error);
      throw error;
    }
  }

  // Update a platform account.
  // account_number and casino_user_id are sent with the same clean numeric value.
  static async updatePlatformAccount(accountId: number | string, casino_user_id: string): Promise<any> {
    try {
      const url = ENDPOINTS.UPDATE_PLATFORM_ACCOUNT(String(accountId));
      const clean = String(casino_user_id || '').replace(/\D/g, '');
      const response = await axiosInstance.put(url, {
        account_number: clean,
        casino_user_id: clean,
      });
      console.log('updatePlatformAccount response:', response.data);
      return response.data;
    } catch (error) {
      console.error('updatePlatformAccount error:', error);
      throw error;
    }
  }

  // Credit coins to a platform account
  static async creditCoinToPlatform(platform_key: string, amount: number): Promise<any> {
    try {
      console.log('creditCoinToPlatform request:', { platform_key, amount });
      const response = await axiosInstance.post(ENDPOINTS.PLATFORM_CREDIT_COIN, {
        platform_key,
        amount,
      });
      console.log('creditCoinToPlatform response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('creditCoinToPlatform error:', error);
      console.error('creditCoinToPlatform error details:', JSON.stringify(error, null, 2));
      // Re-throw with more details
      if (error?.message) {
        throw new Error(error.message);
      }
      throw error;
    }
  }

  // Transfer coins from wallet to casino platform
  static async transferCoins(platform_key: string, amount: number): Promise<any> {
    try {
      console.log('transferCoins request:', { platform_key, amount });
      const response = await axiosInstance.post(ENDPOINTS.TRANSFER_COINS, {
        platform_key,
        amount,
      });
      console.log('transferCoins response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('transferCoins error:', error);
      console.error('transferCoins error details:', JSON.stringify(error, null, 2));
      if (error?.message) {
        throw new Error(error.message);
      }
      throw error;
    }
  }

  // ============= CARD MANAGEMENT ENDPOINTS =============

  // Get all saved cards
  static async getCards(): Promise<CardsResponse> {
    try {
      const response = await axiosInstance.get<CardsResponse>(ENDPOINTS.CARDS);
      console.log('getCards response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('getCards error:', error);
      throw error;
    }
  }

  // Save a new card
  static async saveCard(
    cardholder_name: string,
    stripe_payment_method_id: string,
    is_default: boolean = false
  ): Promise<SaveCardResponse> {
    try {
      const payload = {
        cardholder_name,
        payment_method_id: stripe_payment_method_id,
        is_default,
      };
      console.log('saveCard payload:', payload);
      const response = await axiosInstance.post<SaveCardResponse>(ENDPOINTS.CARDS, payload);
      console.log('saveCard response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('saveCard error:', error);
      throw error;
    }
  }

  // Delete a saved card
  static async deleteCard(cardId: number): Promise<DeleteCardResponse> {
    try {
      const url = ENDPOINTS.CARD_DELETE(cardId);
      const response = await axiosInstance.delete<DeleteCardResponse>(url);
      console.log('deleteCard response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('deleteCard error:', error);
      throw error;
    }
  }

  // ============= UNIFIED COIN PURCHASE ENDPOINTS =============

  // Purchase coins using Stripe
  static async purchaseCoinsStripe(
    pack_id: number,
    amount: number,
    card_id?: number,
    stripe_payment_method_id?: string,
    coins?: number
  ): Promise<CoinPurchaseResponse> {
    try {
      // Validate that card_id is provided
      if (!card_id) {
        throw new Error('Card ID is required for Stripe payments');
      }

      const payload = {
        coin_id: pack_id,  // Backend expects coin_id not pack_id
        payment_method: 'stripe',
        card_id: card_id,  // Backend requires card_id for Stripe payments
      };

      console.log('purchaseCoinsStripe payload:', payload);

      const response = await axiosInstance.post<CoinPurchaseResponse>(
        ENDPOINTS.PURCHASE_COINS,
        payload
      );
      console.log('purchaseCoinsStripe response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('purchaseCoinsStripe error:', error);
      throw error;
    }
  }

  // Purchase coins using Apple IAP
  static async purchaseCoinsApple(
    pack_id: number,
    amount: number,
    transaction_id: string,
    receipt: string,
    coins?: number
  ): Promise<CoinPurchaseResponse> {
    try {
      const payload: any = {
        coin_id: pack_id,  // Backend expects coin_id not pack_id
        payment_method: 'apple',  // Backend expects 'apple' not 'apple_iap'
        apple_transaction_id: transaction_id,  // Backend expects apple_transaction_id
        apple_receipt: receipt,  // Backend expects apple_receipt
      };

      console.log('purchaseCoinsApple payload:', payload);

      const response = await axiosInstance.post<CoinPurchaseResponse>(
        ENDPOINTS.PURCHASE_COINS,
        payload
      );
      console.log('purchaseCoinsApple response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('purchaseCoinsApple error:', error);
      throw error;
    }
  }

  // Purchase coins using Crypto (NowPayments). Returns an invoice_url that
  // must be opened in an in-app WebView, plus order_id/purchase_id for
  // tracking the payment status afterwards.
  static async purchaseCoinsCrypto(
    pack_id: number,
    crypto_currency: string = 'usdttrc20'
  ): Promise<CryptoPurchaseResponse> {
    try {
      const payload = {
        coin_id: pack_id, // Backend expects coin_id not pack_id
        payment_method: 'crypto',
        crypto_currency,
      };

      console.log('purchaseCoinsCrypto payload:', payload);

      const response = await axiosInstance.post<CryptoPurchaseResponse>(
        ENDPOINTS.PURCHASE_COINS,
        payload
      );
      console.log('purchaseCoinsCrypto response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('purchaseCoinsCrypto error:', error);
      throw error;
    }
  }

  // Poll the backend for the status of a crypto purchase. Normalizes the
  // various status strings NowPayments / the backend may return.
  static async getPurchaseStatus(
    purchase_id: number
  ): Promise<{ status: CryptoPaymentStatus; raw: PurchaseStatusResponse | null; newBalance?: string; coins?: string }> {
    try {
      const response = await axiosInstance.get<PurchaseStatusResponse>(
        ENDPOINTS.PURCHASE_STATUS(purchase_id)
      );
      const data = response.data || {};
      const rawStatus = String(
        data?.data?.status || data?.data?.payment_status || data?.status || ''
      ).toLowerCase();

      let status: CryptoPaymentStatus = 'unknown';
      if (['finished', 'completed', 'paid', 'confirmed', 'success', 'succeeded'].includes(rawStatus)) {
        status = 'completed';
      } else if (['failed', 'cancelled', 'canceled', 'refunded'].includes(rawStatus)) {
        status = 'failed';
      } else if (['expired'].includes(rawStatus)) {
        status = 'expired';
      } else if (['confirming', 'sending', 'partially_paid'].includes(rawStatus)) {
        status = 'confirming';
      } else if (['waiting', 'pending', 'new'].includes(rawStatus)) {
        status = 'waiting';
      }

      const newBalanceRaw = data?.data?.new_balance;
      const coinsRaw = data?.data?.coins;
      return {
        status,
        raw: data,
        newBalance: newBalanceRaw != null ? String(newBalanceRaw) : undefined,
        coins: coinsRaw != null ? String(coinsRaw) : undefined,
      };
    } catch (error: any) {
      console.error('getPurchaseStatus error:', error);
      return { status: 'unknown', raw: null };
    }
  }

  // Get purchase history
  static async getPurchaseHistory(page: number = 1, per_page: number = 20): Promise<PurchaseHistoryResponse> {
    try {
      const response = await axiosInstance.get<PurchaseHistoryResponse>(
        ENDPOINTS.PURCHASE_HISTORY,
        {
          params: {
            page,
            per_page,
          },
        }
      );
      console.log('getPurchaseHistory response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('getPurchaseHistory error:', error);
      throw error;
    }
  }

}

export default AuthService;
